import React, { useState } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { motion } from "framer-motion";

interface FeatureListProps {
  features: string[];
}

export function FeatureList({ features }: FeatureListProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const initialCount = 5;
  const hasMore = features.length > initialCount;
  const remainingCount = features.length - initialCount;

  const displayedFeatures = isExpanded ? features : features.slice(0, initialCount);

  return (
    <div className="flex-1 flex flex-col">
      <div className="h-px bg-gradient-to-r from-transparent via-[#ECECEC] to-transparent w-full mb-[10px]" />

      <ul className="space-y-4 mb-4">
        {displayedFeatures.map((feature, i) => (
          <motion.li
            key={i}
            initial={isExpanded && i >= initialCount ? { opacity: 0, y: -10 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: isExpanded && i >= initialCount ? (i - initialCount) * 0.05 : 0 }}
            className="flex items-start gap-4 group cursor-default !mt-[8px]"
          >
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center mt-0.5 shadow-sm group-hover:scale-110 transition-transform duration-300">
              <Check className="h-3.5 w-3.5 text-[#10B981]" strokeWidth={3} />
            </div>
            <span className="text-[#475569] text-sm leading-relaxed font-medium group-hover:text-[#0F172A] transition-colors duration-300">
              {feature}
            </span>
          </motion.li>
        ))}
      </ul>

      {hasMore && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-sm font-semibold text-[#475569] hover:text-[#0F172A] flex items-center justify-center gap-2 py-2 mt-auto transition-colors duration-300 group"
        >
          {isExpanded ? (
            <>
              View Less <ChevronUp className="h-4 w-4 transition-transform" />
            </>
          ) : (
            <>
              View More Features
              <span className="bg-[#10B981]/10 text-[#10B981] px-2 py-0.5 rounded-full text-xs font-bold">
                +{remainingCount}
              </span>
              <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </>
          )}
        </button>
      )}
    </div>
  );
}